import {resource} from '@albert-io/json-api-framework/request/builder';
import {genericMandarkRequest} from 'resources/mandark.resource';
import {List} from 'immutable';
import sessionStore from 'client/Session/SessionStore';

import masqueradeStore from 'generic/Masquerade/Masquerade.store';

export const getAssignmentQuery = (assignmentId: string, isAdmin: boolean, isDraft: boolean) => {
  const modelType = isDraft
    ? {endpoint: 'draft_assignments_v1', metaEndpoint: 'draft_assignment_v1'}
    : {endpoint: 'assignments_v3', metaEndpoint: 'assignment_v3'};
  const teacherId = masqueradeStore.getUserIdByMasqueradeState();

  const isViewingAsAdmin = isAdmin && teacherId === sessionStore.getUserId();

  let baseResource = resource(modelType.endpoint)
    .mandarkEndpoint([modelType.endpoint, assignmentId])
    .include('question_sets_v1.questions_v1')
    .include('question_sets_v1.guide_levels_v2', teacherId)
    .include('question_sets_v1.subject_v2', teacherId)
    .include('classrooms_v1.students_v2.student_assignments_v1')
    .include('classrooms_v1.school_v5')
    .include('students_v2')
    .include('teacher_v1')
    .include('classrooms_v1.teachers_v1.school_v5')
    .include('question_sets_v1.tags_v1')
    .include('question_sets_v1.questions_v3'); // added to get access to question's status
  if (!isDraft) {
    // For published assignments, add both classroom_v1 and assignment_v3 meta to receive assignment stats
    baseResource = baseResource.withMeta('classroom_v1,assignment_v3').meta({
      context: {
        has_lti_course: true,
        lti_deployment: true,
        lti_platform_name: true,
        assignment: {
          stats: true
        }
      }
    });
  } else {
    // For draft assignments, only add classroom_v1 meta
    baseResource = baseResource.withMeta('classroom_v1').meta({
      context: {
        has_lti_course: true,
        lti_deployment: true,
        lti_platform_name: true
      }
    });
  }

  // Conditionally add filter if not super user
  let filteredResource = !sessionStore.isSuper()
    ? baseResource.filter({
        included: {
          ...(!isViewingAsAdmin
            ? {
                students_v2: {
                  classrooms_v1: {
                    teachers_v1: teacherId
                  }
                },
                classrooms_v1: {
                  teachers_v1: teacherId
                }
              }
            : {
                students_v2: {
                  classrooms_v1: {
                    school_v5: {
                      school_personnel_v1: {
                        teacher_v1: {
                          id: teacherId
                        },
                        role_v1: {
                          name: {
                            not: 'teacher'
                          }
                        },
                        status: 'approved'
                      }
                    }
                  }
                },
                classrooms_v1: {
                  school_v5: {
                    school_personnel_v1: {
                      teacher_v1: {
                        id: teacherId
                      },
                      role_v1: {
                        name: {
                          not: 'teacher'
                        }
                      },
                      status: 'approved'
                    }
                  }
                }
              }),
          'classrooms_v1.students_v2.student_assignments_v1': {
            id: assignmentId,
            ...(isViewingAsAdmin ? {} : {teacher_v1: teacherId})
          }
        }
      })
    : baseResource;

  if (isViewingAsAdmin && !sessionStore.isSuper() && !isDraft) {
    filteredResource = filteredResource
      .meta({
        context: {
          user: {
            id: masqueradeStore.getUserIdByMasqueradeState(),
            is_creator_or_classroom_teacher: true
          }
        }
      })
      .filter({
        'meta.user_is_creator_or_classroom_teacher': true
      });
  }

  return genericMandarkRequest('get', filteredResource.done());
};

export const getTeacherSchools = async (teacherId: string) => {
  const query = getTeacherWithMetadataQuery(teacherId);
  try {
    const teacher = await query.getResourcePromise();
    const schoolLicensesInfo = teacher.getMeta().getSchoolLicensesInfo();
    const schoolPromises = schoolLicensesInfo
      .filter((licenseInformation) => {
        const hasSchoolLicense = licenseInformation.get('has_school_license');
        const licenseStatus = licenseInformation.get('school_license_status');
        return hasSchoolLicense && licenseStatus === 'active';
      })
      .map((approvedlicenseInformation) =>
        getSchoolByIdQuery(approvedlicenseInformation.get('school_id'))
      );
    const schools = await Promise.all(schoolPromises);
    return List(schools);
  } catch (_err) {
    return List([]);
  }
};

// imported from marketplace/src/client/AccountSettings/Subscriptions/InstructionalLicenseCard.js
const getSchoolByIdQuery = (id) => {
  return resource('school_v5').mandarkEndpoint(['schools_v5', id]).getResourcePromise();
};

const getTeacherWithMetadataQuery = (teacherId) => {
  return resource('teachers_v1')
    .mandarkEndpoint(['teachers_v1', teacherId])
    .include('school_v5')
    .include('school_personnel_v1')
    .include('school_personnel_v1.school_v5')
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true
          }
        }
      }
    });
};
